<div class="page flex flex-col items-center">
    
    <div class="w-full max-w-md">
        <!-- Logo/Header -->
        <div class="text-center mb-10">
            <div class="flex flex-col items-center justify-center mb-4 text-accent">
                <app-brand-logo class="h-20 w-20 mb-2" />
                <span class="brand-wordmark text-3xl text-slate-900 dark:text-slate-100">CANI<span class="brand-apostrophe">'</span>TRACK</span>
            </div>
            <p class="text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest text-xs">{{ 'register.subtitle' | translate }}</p>
        </div>

        @if (registeredEmail(); as registered) {
            <div class="card p-10 text-center">
                <div class="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h1 class="auth-title mb-4">{{ 'register.success.title' | translate }}</h1>
                <p class="text-slate-500 dark:text-slate-400 mb-8 font-medium">
                    {{ 'register.success.desc' | translate }}
                </p>
                <p class="-mt-4 mb-8 break-all font-bold text-slate-900 dark:text-white">{{ registered }}</p>
                @if (resent()) {
                    <app-auth-alert tone="success" class="mb-6 block text-left">{{ 'auth.confirm.resent' | translate: registered }}</app-auth-alert>
                }
                @if (errorMessage(); as msg) {
                    <app-auth-alert class="mb-6 block text-left">{{ msg | translate }}</app-auth-alert>
                }
                <button type="button" routerLink="/login" class="btn btn-primary w-full">
                    {{ 'register.success.button' | translate }}
                </button>
                @if (!resent()) {
                    <button type="button" (click)="resendConfirmation()" [disabled]="isResending()" class="btn btn-secondary mt-3 w-full">
                        {{ (isResending() ? 'auth.confirm.resending' : 'auth.confirm.resend') | translate }}
                    </button>
                }
            </div>
        } @else {
            <div class="card relative overflow-hidden p-8 md:p-10">
                <h1 class="auth-title mb-8">{{ 'register.title' | translate }}</h1>

                <form [formGroup]="registerForm" (ngSubmit)="onSubmit()" class="space-y-6 relative z-10" novalidate>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label for="firstName" class="field-label">{{ 'register.firstName' | translate }}</label>
                            <input type="text" id="firstName" formControlName="firstName" autocomplete="given-name"
                                class="field">
                            @if (fieldError(registerForm.controls.firstName); as err) {
                                <p class="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">{{ err.key | translate: err.arg }}</p>
                            }
                        </div>
                        <div>
                            <label for="lastName" class="field-label">{{ 'register.lastName' | translate }}</label>
                            <input type="text" id="lastName" formControlName="lastName" autocomplete="family-name"
                                class="field">
                            @if (fieldError(registerForm.controls.lastName); as err) {
                                <p class="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">{{ err.key | translate: err.arg }}</p>
                            }
                        </div>
                    </div>

                    <div>
                        <label for="dob" class="field-label">{{ 'register.dob' | translate }}</label>
                        <input type="date" id="dob" formControlName="dob" autocomplete="bday"
                            class="field">
                        @if (fieldError(registerForm.controls.dob); as err) {
                            <p class="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">{{ err.key | translate: err.arg }}</p>
                        }
                    </div>

                    <div>
                        <label for="email" class="field-label">{{ 'register.email' | translate }}</label>
                        <input type="email" id="email" formControlName="email" autocomplete="email" [placeholder]="'register.emailPlaceholder' | translate"
                            class="field">
                        @if (fieldError(registerForm.controls.email); as err) {
                            <p class="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">{{ err.key | translate: err.arg }}</p>
                        }
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label for="password" class="field-label">{{ 'register.password' | translate }}</label>
                            <input type="password" id="password" formControlName="password" autocomplete="new-password"
                                class="field">
                            @if (fieldError(registerForm.controls.password); as err) {
                                <p class="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">{{ err.key | translate: err.arg }}</p>
                            } @else {
                                <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">{{ 'auth.passwordHint' | translate: minPasswordLength }}</p>
                            }
                        </div>
                        <div>
                            <label for="confirmPassword" class="field-label">{{ 'register.confirmPassword' | translate }}</label>
                            <input type="password" id="confirmPassword" formControlName="confirmPassword" autocomplete="new-password"
                                class="field">
                            @if (fieldError(registerForm.controls.confirmPassword); as err) {
                                <p class="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">{{ err.key | translate: err.arg }}</p>
                            }
                            @if (registerForm.hasError('passwordsMismatch') && registerForm.controls.confirmPassword.touched) {
                                <p class="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">{{ 'auth.field.mismatch' | translate }}</p>
                            }
                        </div>
                    </div>

                    @if (errorMessage(); as msg) {
                        <app-auth-alert>
                            <p>{{ msg | translate }}</p>
                            @if (msg === 'auth.error.userAlreadyExists') {
                                <p class="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                                    <a routerLink="/login" class="underline underline-offset-2 hover:no-underline">{{ 'register.loginLink' | translate }}</a>
                                    <a routerLink="/forgot-password" [queryParams]="{ email: registerForm.controls.email.value }" class="underline underline-offset-2 hover:no-underline">{{ 'auth.forgot.link' | translate }}</a>
                                </p>
                            }
                        </app-auth-alert>
                    }

                    <button type="submit" [disabled]="isLoading()"
                        class="btn btn-primary w-full">
                        @if (isLoading()) {
                            <app-branded-loader mode="inline" [label]="'register.loading' | translate" />
                        } @else {
                            {{ 'register.button' | translate }}
                        }
                    </button>

                    <p class="text-center text-slate-500 dark:text-slate-400 text-xs font-bold pt-4">
                        {{ 'register.footer' | translate }} <a routerLink="/login" class="link-accent">{{ 'register.loginLink' | translate }}</a>
                    </p>
                </form>
            </div>
        }

        <!-- Footer Info -->
        <p class="mt-8 text-center text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em]">
            &copy; 2026 Cani'Track - Performance & Community
        </p>
    </div>
</div>
